import * as React from 'react';

import { cn } from '../lib/utils';

/**
 * White surface, 12px radius, hairline border. Padding is 1rem 1.25rem, applied
 * by the header/content/footer parts rather than the shell, so a card can hold
 * a full-bleed table when it needs to.
 */
const Card = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'rounded-lg border-hairline border-border bg-card text-card-foreground shadow-card',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'relative flex flex-col space-y-1 px-5 py-4',
      // The violet rule that marks every card header. Inset vertically so it
      // never collides with the card's 12px corner radius.
      'before:absolute before:inset-y-4 before:left-0 before:w-[3px] before:rounded-r-full before:bg-primary',
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

/** 15px / 500 — the reference weight for a card title. */
const CardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('text-title font-medium tracking-tight text-foreground', className)}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-meta text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('px-5 py-4 pt-0', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center px-5 py-4 pt-0', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
